import { useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';
import { usePageParam } from '@/hooks';
import { PaginatedResponse } from '@/types/responses';
import { PaginatedEventsResult } from './eventsDataShared';

interface UsePaginatedListParams<T> {
    cacheKey: string;
    pageSize: number;
    fetchPage: (pageIndex: number) => Promise<PaginatedResponse<T>>;
}

export function usePaginatedList<T>({
    cacheKey,
    pageSize,
    fetchPage,
}: UsePaginatedListParams<T>): PaginatedEventsResult<T> {
    const [page, setPage] = usePageParam();

    const previousCacheKey = useRef(cacheKey);
    useEffect(() => {
        if (previousCacheKey.current !== cacheKey) {
            previousCacheKey.current = cacheKey;
            if (page !== 1) setPage(1);
        }
    }, [cacheKey, page, setPage]);

    const { data, error, isLoading, isValidating, mutate } = useSWR(
        [cacheKey, 'page', page],
        () => fetchPage(page - 1),
        {
            revalidateOnFocus: false,
            revalidateIfStale: false,
            revalidateOnMount: true,
            dedupingInterval: 0,
            keepPreviousData: true,
        },
    );

    const events = useMemo(() => data?.items ?? [], [data]);
    const totalItems = data?.page.totalItems ?? 0;

    const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 0;
    useEffect(() => {
        if (totalPages > 0 && page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page, setPage]);

    return {
        events,
        totalItems,
        page,
        pageSize,
        onPageChange: setPage,
        error,
        isLoading,
        isFetching: isValidating,
        mutate,
    };
}
